import { Theme, makeStyles, createStyles } from '@material-ui/core/styles';
import { BrandColors } from '../../constants';
import { TemplateSettings } from '../../store/interfaces';

export type BasicTaskListStyleProps = {
  isICPEnabled: boolean;
  settings: TemplateSettings;
};

export const BasicTaskList = makeStyles((theme: Theme) => {
  return createStyles({
    container: {
      marginLeft: 'auto',
      marginRight: 'auto',
      [theme.breakpoints.up('lg')]: {
        maxWidth: '864px',
      },
      [theme.breakpoints.up('xl')]: {
        maxWidth: '1140px',
      },
    },
    grid: {
      width: '100%',
      [theme.breakpoints.up('lg')]: {
        padding: '2rem 0',
      },
      [theme.breakpoints.up('xl')]: {
        padding: '5rem 0',
      },
    },
    paper: (props: BasicTaskListStyleProps) => ({
      borderRadius: '5px',
      // ICP
      [theme.breakpoints.down('sm')]: {
        paddingBottom: props.isICPEnabled ? '4rem' : '',
      },
      [theme.breakpoints.up('sm')]: {
        padding: '2.5rem 1.5rem',
      },
      [theme.breakpoints.up('md')]: {
        padding: '2.5rem 5rem',
      },
      [theme.breakpoints.up('lg')]: {
        padding: '20px',
      },
    }),
    taskListHeader: {
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
      },
      [theme.breakpoints.up('sm')]: {
        alignItems: 'center',
      },
    },
    accordionWrapper: {
      marginTop: '2rem',
    },
    accordion: (props: BasicTaskListStyleProps) => ({
      border: `1px solid ${props.settings?.otherElements?.borderColor}`,
      marginBottom: '1rem',
      borderRadius: '5px',
      '& .Mui-disabled': {
        opacity: '1 !important',
      },
    }),
    accordionSummary: (props: BasicTaskListStyleProps) => ({
      '&.Mui-expanded': {
        borderBottom: `1px solid ${props.settings?.otherElements?.borderColor}`,
      },
    }),
    accordionDetails: {
      flexDirection: 'column',
    },
    alertBox: (props: BasicTaskListStyleProps) => ({
      color: 'inherit',
      background: `${props.settings?.otherElements?.disabledColor}`,
      border: `1px solid ${props.settings.otherElements?.borderColor}`,
      marginBottom: '0.9375rem',
      '& .MuiAlert-icon': {
        color: 'inherit',
      },
    }),
    hideAlert: {
      display: 'none',
    },
    progressIcon: {
      marginLeft: '0.35rem',
      fontSize: '12px',
      [theme.breakpoints.up('sm')]: {
        fontSize: '16px',
      },
    },
    emptyCircleIcon: (props: BasicTaskListStyleProps) => ({
      display: 'inline-block',
      height: '1em',
      overflow: 'visible',
      verticalAlign: '-0.19em',
      '& svg': {
        '& circle': {
          fill: `${props.settings.otherElements?.ProgressIconColor?.fillColor}`,
          stroke: `${props.settings.otherElements?.ProgressIconColor?.strokeColor}`,
        },
      },
    }),
    timerIcon: {
      fontSize: '1.25rem',
      margin: '-0.25rem 0.625rem',
    },
    card: (props: BasicTaskListStyleProps) => ({
      margin: '0.75rem 0.25rem 0.5rem',
      border: `1px solid ${props.settings.otherElements?.borderColor}`,
      borderRadius: '5px',
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.up('sm')]: {
        padding: '1.375rem',
        minHeight: '5.625rem',
      },
    }),
    lock: {
      '& > path': {
        fill: theme.palette.basicTaskListIcons?.lockAndCompleted.color,
      },
      display: 'inline-flex',
      // ie fix
      '@media screen and (-ms-high-contrast: none)': {
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        margin: 'auto',
        width: '77px',
        height: '77px',
      },
    },
    timerText: (props: BasicTaskListStyleProps) => ({
      fontSize: '14px',
      paddingRight: '0.5rem',
      [theme.breakpoints.up('sm')]: {
        borderRight: `1px solid ${props.settings.otherElements?.borderColor}`,
      },
    }),
    timeValue: {
      fontWeight: 700,
    },
    cardContent: {
      paddingLeft: '1rem',
    },
    cardTitle: {
      fontWeight: 700,
      fontSize: '0.75rem',
      [theme.breakpoints.up('sm')]: {
        fontSize: '0.875rem',
      },
    },
    description: {
      fontSize: '0.75rem',
    },
    headerDescriptionText: {
      fontSize: '1rem',
      '& p': {
        fontSize: '1rem',
      },
    },

    typographyCation: {
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        textAlign: 'right',
      },
    },
    positionRelative: {
      position: 'relative',
    },
    accordionSummaryAbsoluteChild: {
      [theme.breakpoints.up('sm')]: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      },
      [theme.breakpoints.down('xs')]: {
        textAlign: 'left',
      },
    },
    marginBottom20: {
      [theme.breakpoints.up('sm')]: {
        marginBottom: '0.75rem',
      },
    },
    actionArea: {
      '&:hover $focusHighlight': {
        opacity: 0,
      },
    },
    focusHighlight: {},
    filledProgressDot: {
      '& > path': {
        fill: theme.palette.primary.main,
      },
    },
    completedIcon: {
      '& > path': {
        fill: theme.palette.basicTaskListIcons?.lockAndCompleted.color,
      },
    },
    verticalAlignTop: {
      verticalAlign: 'text-top',
      marginRight: '4px',
    },
    floatRight: {
      float: 'right',
      marginRight: '0.50rem',
    },
    stageDisabledColor: (props: BasicTaskListStyleProps) => ({
      backgroundColor: `${props.settings?.otherElements?.disabledColor} !important`,
    }),

    //Mobile design start from here
    width100: {
      [theme.breakpoints.down('xs')]: {
        width: '100%',
      },
    },
    textAlignCenter: {
      textAlign: 'center',
    },
    textAlignRight: {
      textAlign: 'right',
    },
    headerGrid: {
      [theme.breakpoints.down('xs')]: {
        marginTop: '0.625rem',
      },
    },
    alignRight: {
      textAlign: 'right',
    },
    accordionContentPaddingMobile: {
      padding: '0.625rem',
    },
    taskTitle: {
      fontWeight: 600,
      fontSize: '0.875rem',
      [theme.breakpoints.up('sm')]: {
        fontSize: '1rem',
      },
    },
    cardContentMobile: {
      borderBottom: `1px solid ${BrandColors.borderGrey}`,
    },
    mobileCard: {
      margin: '0.75rem 0.25rem 0.5rem',
      borderRadius: '5px',
      display: 'flex',
      alignItems: 'center',
    },
    fullLengthButton: {
      width: '100%',
    },
    completedTitle: {
      fontSize: '12px',
      fontWeight: 600,
    },
    reponseReuseLabel: {
      fontWeight: 700,
      fontSize: '.8rem',
      lineHeight: '.7rem',
      [theme.breakpoints.up('sm')]: {
        paddingTop: '0.7rem',
      },
      '& > strong': {
        borderSpacing: '-0.11em',
        borderBottom:
          theme.palette.dialogBox?.border === 'none'
            ? `1px solid ${theme?.palette?.dialogBox.secondaryColor}`
            : theme.palette.dialogBox?.border,
      },
    },
  });
});
